/**
 * useSerialTerminal — sessão interativa de terminal serial.
 *
 * Diferente dos hooks de produção (que abrem/enviam/leem/fecham por comando), aqui a porta
 * permanece ABERTA enquanto a sessão estiver conectada, com um loop de leitura contínuo que
 * empurra cada linha recebida para o terminal em tempo real. O usuário pode enviar qualquer
 * comando livre; o eco do comando enviado e as respostas brutas do dispositivo são entregues
 * via callback `onLine`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSerialPort } from '@/hooks/useSerialPort';

const LINE_TERMINATOR = '\r\n';

// Baud rates comuns oferecidos na UI; o primeiro é o padrão.
export const BAUD_RATE_OPTIONS = [115200, 9600, 19200, 38400, 57600, 230400, 460800] as const;
export const STOP_BITS_OPTIONS = [1, 2] as const satisfies readonly (1 | 2)[];

export const DEFAULT_BAUD_RATE = 115200;
export const DEFAULT_STOP_BITS: 1 | 2 = 2;

export interface SerialConnectionOptions {
  baudRate: number;
  stopBits: 1 | 2;
}

export type TerminalLineKind = 'sent' | 'received' | 'info' | 'error';

export interface TerminalLine {
  kind: TerminalLineKind;
  text: string;
}

interface UseSerialTerminalProps {
  /** Chamado para cada linha (enviada, recebida ou informativa) produzida na sessão. */
  onLine?: (line: TerminalLine) => void;
}

export const useSerialTerminal = ({ onLine }: UseSerialTerminalProps = {}) => {
  const { requestPort, openPort, closePort, getReader, isSupported } = useSerialPort({});

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const portRef = useRef<ReturnType<typeof requestPort> extends Promise<infer P> ? P : never>(
    undefined as never,
  );
  // Writer persistente da sessão: obtido uma vez no connect e liberado no disconnect.
  // Pegar/soltar um writer a cada comando disputa o lock com o loop de leitura e deixa o
  // writable indisponível ("Writable stream not available") nos envios seguintes.
  const writerRef = useRef<WritableStreamDefaultWriter<Uint8Array> | null>(null);
  const encoderRef = useRef(new TextEncoder());
  const readLoopActiveRef = useRef(false);
  // Buffer de bytes ainda não terminados por \r\n entre iterações do loop de leitura.
  const bufferRef = useRef('');
  // onLine pode mudar a cada render; mantemos a referência atual sem reiniciar o loop.
  const onLineRef = useRef(onLine);
  useEffect(() => {
    onLineRef.current = onLine;
  }, [onLine]);

  const emit = useCallback((kind: TerminalLineKind, text: string) => {
    onLineRef.current?.({ kind, text });
  }, []);

  // Loop de leitura contínuo: roda enquanto a porta estiver conectada, quebrando o stream
  // em linhas por \r\n e emitindo cada uma como 'received'. Remove caracteres de controle
  // (mesma limpeza usada na leitura do BWS 4G) preservando \r\n.
  const startReadLoop = useCallback(async () => {
    const port = portRef.current;
    if (!port || readLoopActiveRef.current) return;
    readLoopActiveRef.current = true;

    const decoder = new TextDecoder('utf-8', { fatal: false, ignoreBOM: true });

    try {
      while (readLoopActiveRef.current && port.readable) {
        const reader = await getReader(port);
        try {
          while (readLoopActiveRef.current) {
            const { value, done } = await reader.read();
            if (done) break;
            if (!value?.byteLength) continue;

            let chunk = decoder.decode(value, { stream: true });
            chunk = chunk.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
            bufferRef.current += chunk;

            const lines = bufferRef.current.split('\r\n');
            bufferRef.current = lines.pop() ?? '';
            for (const line of lines) {
              if (line.length > 0) emit('received', line);
            }
          }
        } finally {
          try {
            await reader.cancel();
          } catch {
            /* já cancelado */
          }
          try {
            reader.releaseLock();
          } catch {
            /* já released */
          }
        }
      }
    } catch (error) {
      // Erros de leitura após desconexão intencional são esperados — silencia quando o loop
      // já foi encerrado; caso contrário, reporta (ex: cabo removido durante a sessão).
      if (readLoopActiveRef.current) {
        emit('error', `Leitura interrompida: ${(error as Error).message}`);
        setIsConnected(false);
      }
    }
  }, [emit, getReader]);

  const connect = useCallback(
    async (options?: SerialConnectionOptions) => {
      const baudRate = options?.baudRate ?? DEFAULT_BAUD_RATE;
      const stopBits = options?.stopBits ?? DEFAULT_STOP_BITS;
      if (!isSupported) {
        emit('error', 'Web Serial API não suportada neste navegador. Use Chrome ou Edge.');
        return;
      }
      if (isConnected || isConnecting) return;

      setIsConnecting(true);
      try {
        const port = await requestPort();
        if (!port) {
          // Usuário cancelou o seletor de portas — não é erro.
          return;
        }
        await openPort(port, { baudRate, stopBits });
        if (!port.writable) {
          throw new Error('Porta sem stream de escrita disponível');
        }
        portRef.current = port;
        // Mantém o writer preso durante toda a sessão — liberado apenas no disconnect.
        writerRef.current = port.writable.getWriter();
        bufferRef.current = '';
        setIsConnected(true);
        emit(
          'info',
          `Conectado (${baudRate} baud, ${stopBits} stop bit${stopBits > 1 ? 's' : ''}).`,
        );
        void startReadLoop();
      } catch (error) {
        emit('error', `Falha ao conectar: ${(error as Error).message}`);
        portRef.current = undefined as never;
      } finally {
        setIsConnecting(false);
      }
    },
    [emit, isConnected, isConnecting, isSupported, openPort, requestPort, startReadLoop],
  );

  const disconnect = useCallback(async () => {
    readLoopActiveRef.current = false;
    const port = portRef.current;
    portRef.current = undefined as never;
    bufferRef.current = '';
    setIsConnected(false);

    // Libera o writer ANTES de fechar a porta — um writer preso impede o port.close().
    const writer = writerRef.current;
    writerRef.current = null;
    if (writer) {
      try {
        await writer.close();
      } catch {
        /* stream já encerrada */
      }
      try {
        writer.releaseLock();
      } catch {
        /* já released */
      }
    }

    if (port) {
      try {
        await closePort(port);
        emit('info', 'Desconectado.');
      } catch (error) {
        emit('error', `Falha ao desconectar: ${(error as Error).message}`);
      }
    }
  }, [closePort, emit]);

  const sendCommand = useCallback(
    async (raw: string) => {
      const command = raw.trim();
      if (!command) return;

      // Usa o writerRef (sempre atual) como fonte de verdade — não o estado isConnected,
      // que pode estar stale no closure deste callback.
      const writer = writerRef.current;
      if (!writer) {
        emit('error', 'Nenhum dispositivo conectado.');
        return;
      }

      try {
        await writer.write(encoderRef.current.encode(`${command}${LINE_TERMINATOR}`));
        emit('sent', command);
      } catch (error) {
        emit('error', `Falha ao enviar comando: ${(error as Error).message}`);
      }
    },
    [emit],
  );

  // Encerra a sessão APENAS ao desmontar. Deps vazias de propósito: as funções de
  // useSerialPort (closePort etc.) são recriadas a cada render, então depender delas faria
  // o cleanup rodar no meio da sessão — fechando a porta logo após conectar. Aqui fechamos a
  // porta diretamente via port.close(), sem depender da identidade de closePort.
  useEffect(() => {
    return () => {
      readLoopActiveRef.current = false;
      const writer = writerRef.current;
      writerRef.current = null;
      if (writer) {
        writer.close().catch(() => undefined);
        try {
          writer.releaseLock();
        } catch {
          /* já released */
        }
      }
      const port = portRef.current;
      portRef.current = undefined as never;
      if (port) {
        void port.close().catch(() => undefined);
      }
    };
  }, []);

  return {
    isSupported,
    isConnected,
    isConnecting,
    connect,
    disconnect,
    sendCommand,
  };
};

export default useSerialTerminal;
